import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  debutDeLAnnee,
  densiteParJour,
  evenementsDansLaPlage,
  finDeLAnnee,
  finDuMois,
  formaterJour,
  joursDeLaSemaine,
  moisDeLAnnee,
  semainesDuMois,
} from './periodes.js';
import type { PropsVue } from './types.js';
import styles from './Calendrier.module.css';

/** Le niveau de couleur d'un jour : 0 (rien) à 4 (quatre événements ou plus). */
function niveau(compte: number): number {
  return Math.min(4, compte);
}

/**
 * L'année : douze mini-mois dont chaque jour porte sa densité (nombre
 * d'événements qui le couvrent). Le titre du mois ouvre le mois, un jour
 * ouvre sa semaine.
 */
export function VueAnnee({
  ancre,
  evenements,
  aujourdhui,
  langue,
  onAller,
}: PropsVue): JSX.Element {
  const { t } = useTranslation('regie');
  const densite = densiteParJour(evenements, debutDeLAnnee(ancre), finDeLAnnee(ancre));
  return (
    <div
      className={styles.annee}
      data-ui="calendrier-annee"
      role="group"
      aria-label={t('calendrier.grille.annee')}
    >
      {moisDeLAnnee(ancre).map((premier) => {
        const nombre = evenementsDansLaPlage(evenements, premier, finDuMois(premier)).length;
        const nomDuMois = formaterJour(premier, langue, { month: 'long' });
        return (
          <section key={premier} className={styles.miniMois} data-ui="calendrier-mini-mois">
            <h3 className={styles.miniMoisTitre}>
              <button
                type="button"
                className={styles.miniMoisBouton}
                aria-label={t('calendrier.ouvrirMois', {
                  mois: formaterJour(premier, langue, { month: 'long', year: 'numeric' }),
                  evenements: t('calendrier.evenements', { count: nombre }),
                })}
                onClick={() => onAller('mois', premier)}
              >
                <span>{nomDuMois}</span>
                <span className={styles.miniMoisCompte} aria-hidden>
                  {nombre}
                </span>
              </button>
            </h3>
            <div className={styles.miniGrille} aria-hidden>
              {joursDeLaSemaine(premier).map((jour) => (
                <span key={jour} className={styles.miniEntete}>
                  {formaterJour(jour, langue, { weekday: 'narrow' })}
                </span>
              ))}
              {semainesDuMois(premier).flatMap((semaine) =>
                semaine.map((jour) =>
                  jour.slice(0, 7) === premier.slice(0, 7) ? (
                    <span
                      key={jour}
                      className={styles.miniJour}
                      data-niveau={niveau(densite.get(jour) ?? 0)}
                      data-aujourdhui={jour === aujourdhui ? 'oui' : undefined}
                      title={`${formaterJour(jour, langue, { day: 'numeric', month: 'long' })} — ${t(
                        'calendrier.evenements',
                        { count: densite.get(jour) ?? 0 },
                      )}`}
                      onClick={() => onAller('semaine', jour)}
                    >
                      {Number(jour.slice(8))}
                    </span>
                  ) : (
                    <span key={jour} className={styles.miniJourVide} />
                  ),
                ),
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
